// Copyright ©️ 2026 eVolve MEP, LLC

import { Fragment } from 'react';

import type { TaskType } from '@evolvemep/api-ts';
import { Divider, Loader, Stack, Text } from '@mantine/core';

import { isNotNil } from 'helpers/isNotNil';
import { TriggerOnVisible } from 'hooks/useOnScreen';
import { useTaskTypeModals } from 'modules/Shop/Tasks/TaskTypes/modalRegistries';
import { TaskTypeListItem } from 'modules/Shop/Tasks/TaskTypes/TaskTypeListItem';
import type { SelectedTasksAction } from 'modules/Shop/Tasks/TaskTypes/useSelectedTasks';

type Props = {
  loading: boolean;
  groupTasks: TaskType[];
  entireCount: number | undefined;
  nothingFoundText?: string;
  fetchNextPage?: () => void;
  selectedTasks: TaskType[];
  updateSelectedTasks: (action: SelectedTasksAction) => void;
  /** Called after a task type in the list is edited or deleted. */
  onTaskTypesChanged: () => void;
};

export const TaskTypeList = ({
  groupTasks,
  loading,
  entireCount,
  nothingFoundText = 'No tasks in group.',
  fetchNextPage,
  selectedTasks,
  updateSelectedTasks,
  onTaskTypesChanged,
}: Props) => {
  const { openModal } = useTaskTypeModals();
  return (
    <Stack gap={0} mih={94}>
      {groupTasks.map((t, index) => (
        <Fragment key={t.taskTypeId}>
          <TaskTypeListItem
            key={t.taskTypeId}
            task={t}
            isSelected={selectedTasks.some((s) => s.taskTypeId === t.taskTypeId)}
            onTaskSelectToggle={(taskType) => updateSelectedTasks({ type: 'toggle', taskType })}
            onEditTaskType={(taskType) =>
              openModal('CreateEditTaskTypeModal', {
                taskType,
                onTaskTypeUpdated: (updated) => {
                  updateSelectedTasks({ type: 'taskTypeUpdated', taskType: updated });
                  onTaskTypesChanged();
                },
              })
            }
            onDeleteTaskType={(taskType) =>
              openModal('DeleteTaskTypeModal', {
                taskType,
                onTaskTypeDeleted: (deleted) => {
                  updateSelectedTasks({ type: 'taskTypeDeleted', taskType: deleted });
                  onTaskTypesChanged();
                },
              })
            }
          />
          {index + 1 !== groupTasks.length && <Divider />}
        </Fragment>
      ))}
      <Stack flex={1} align="center" justify="center">
        {entireCount === 0 && (
          <Text fz="sm" c="dimmed">
            {nothingFoundText}
          </Text>
        )}
        {isNotNil(fetchNextPage) ? (
          <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
        ) : (
          loading && <Loader type="dots" />
        )}
      </Stack>
    </Stack>
  );
};
